import { inject } from '@angular/core';
import {
	ActivatedRouteSnapshot,
	CanActivateFn,
	createUrlTreeFromSnapshot,
	Router,
	RouterStateSnapshot
} from '@angular/router';
import { NgxI18nService } from '@studiohyperdrive/ngx-i18n';
import { ObservableBoolean } from '@studiohyperdrive/rxjs-utils';
import { of } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';

import { AuthenticationService } from '@vlaio/shared/authentication';
import { sharedI18nKeys } from '@vlaio/shared/i18n';
import { ToastService } from '@vlaio/shared/ui/toast';

import { UserAccessKeys, UserService } from '../../data';

/**
 * Guard that checks if the logged in user has the correct access. If they are not logged in, they get redirected. After login,
 * we try again. If the user does not have access, they are redirected to the parent page.
 *
 * @param route The route that is being activated
 * @returns Whether the user is allowed to access the route. If not, they are redirected to the parent page until eventually either the dashboard or the home page.
 */
export const HasAccessGuard: CanActivateFn = (
	route: ActivatedRouteSnapshot,
	state: RouterStateSnapshot
): ObservableBoolean => {
	// Wouter: Inject the necessary services
	const userService: UserService = inject(UserService);
	const router: Router = inject(Router);
	const i18nService: NgxI18nService = inject(NgxI18nService);
	const toastService: ToastService = inject(ToastService);
	const authService: AuthenticationService = inject(AuthenticationService);

	// Wouter: If the url is left empty, the user is redirected to the dashboard
	const redirectTo: string[] = route.data?.redirect || ['../'];
	// Wouter: The required access keys are defined in the route data
	const requiredAccessKey: UserAccessKeys[] = route.data?.accessKeys || '';
	// Wouter: Whether all the access keys should be present
	const requireAllAccessKeys = route.data?.requireAllAccessKeys ?? true;

	// Wouter: Check if the user has the required access.
	return userService.user$.pipe(
		take(1),
		switchMap((user) => {
			// Wouter: Note: this will only work for routes defined in the bypass-guard.util.ts file
			if (!user) {
				// Wouter: If the user is not logged in, we log the user in and redirect them to the page using the callback
				return authService.login({
					customCallBack: {
						url: state.url.split('?')[0],
						params: route.queryParams
					}
				});
			}

			return of(user);
		}),
		// Wouter: After the user is logged in, we check if they have the required access
		switchMap(() => userService.hasAccess(requireAllAccessKeys, ...requiredAccessKey)),
		tap((hasAccess) => {
			if (!hasAccess) {
				// Wouter: After redirect, open a toast that the user is not allowed access.
				router.navigateByUrl(createUrlTreeFromSnapshot(route, redirectTo)).then(() => {
					toastService.openFromComponent({
						type: 'negative',
						text: i18nService.getTranslation(sharedI18nKeys.Common.Fallback)
					});
				});
			}
		})
	);
};
